import { Icon, type IconId } from "@/components/ui/Icon";
import { iconIdByTagKey } from "@caliu/styles/icons";
import { tagColorClassName, tagColorStyle } from "./tagColors";

// The tag-icon registry is the Espurna catalog (packages/styles/icons): every
// entry with a `tag` key is pickable, grouped in six categories. The key is the
// synced `Tag.icon` value and never changes; Swift reads the same catalog
// (EspurnaIcon.byTagKey), so both clients render the same drawing.

/** The Espurna icon for a synced tag-icon key, or null for an unknown key. */
export function tagIconId(key: string | null | undefined): IconId | null {
  if (!key) return null;
  return (iconIdByTagKey[key] as IconId | undefined) ?? null;
}

export function TagIcon({ name, size = 13 }: { name: string; size?: number }) {
  const id = tagIconId(name);
  // An unknown key (a newer client's icon) falls back to the plain tag icon,
  // same as Swift. Known tag icons always show their tint, in the tag's colour
  // (Espurna README, "Tint").
  if (!id) return <Icon name="tag" size={size} />;
  return <Icon name={id} size={size} />;
}

/**
 * A tag as a title: its icon in its colour, then its full path. Heads the tag
 * dialogs, so it is clear which tag is being edited (same as the note list header).
 */
export function TagTitle({
  tag,
  path,
  iconSize = 14,
}: {
  tag: { icon?: string | null; color?: string | null };
  path: string;
  iconSize?: number;
}) {
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <span
        className={`flex-shrink-0 flex items-center ${tag.color ? (tagColorClassName(tag.color) ?? "") : "text-muted"}`}
        style={tagColorStyle(tag.color)}
      >
        {tag.icon ? <TagIcon name={tag.icon} size={iconSize} /> : <Icon name="tag" size={iconSize} />}
      </span>
      <span className="truncate min-w-0">{path}</span>
    </span>
  );
}
